import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useHistory } from "react-router-dom";

import projectApi from "../../../api/project";
import { Loader, toast } from "../../../common/components/core";
import {
    useAuthErrorHandlerCreator,
    useModalWithData,
} from "../../../common/hooks";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
} from "../../../utils/errorHandling";
import { ProjectAccountFormValues } from "../components/ProjectAccountForm";

type UpdateProjectAccountHandler = {
    isSubmitting: boolean;
    handler: (formValues: ProjectAccountFormValues) => void;
};

const useUpdateProjectAccountHandler = (): UpdateProjectAccountHandler => {
    const history = useHistory();
    const [hasError, setHasError] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const onAuthError = useAuthErrorHandlerCreator();

    const [show, hide] = useModalWithData<ProjectAccountFormValues>(
        (formValues) => () => {
            const confirm = async () => {
                if (!formValues) {
                    hide();
                    return;
                }

                setSubmitting(true);
                try {
                    await projectApi.bulkUpdateAccounts(formValues);
                    hide();
                    history.push("/collection");
                } catch (e) {
                    setSubmitting(false);
                    composeErrorHandlers(
                        onAuthError,
                        onStaleStateError(() => {
                            hide();
                            history.push("/collection");
                            toast(
                                "Project folder has been deleted by Project Officer.",
                            );
                        }),
                    )(e as GenericError);
                }
            };

            const dismiss = () => {
                setHasError(false);
                hide();
            };

            return (
                <ThemeProviderV2>
                <Modal show onHide={hide} backdrop="static" centered>
                    <Loader overlay isLoading={isSubmitting}>
                        <Modal.Header>
                            {hasError ? (
                                <h2 className="mb-0">
                                    Update Failed
                                </h2>
                            ) : (
                                <h2 className="mb-0">
                                    Update Bank Account Details
                                </h2>
                            )}
                        </Modal.Header>
                        <Modal.Body>
                            {hasError ? (
                                <p>
                                    Please update the error field(s) and try
                                    again.
                                </p>
                            ) : (
                                <p>
                                    Are you sure you want to update the bank
                                    account details for the projects?
                                </p>
                            )}
                        </Modal.Body>
                        <Modal.Footer className="d-flex justify-content-between px-3">
                            {hasError ? (
                                <Button variant="light.primary" onClick={dismiss}>
                                    Dismiss
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        variant="light.tertiary"
                                        onClick={dismiss}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="light.primary" onClick={confirm}>
                                        Confirm
                                    </Button>
                                </>
                            )}
                        </Modal.Footer>
                    </Loader>
                </Modal>
                </ThemeProviderV2>
            );
        },
        [hasError, history, isSubmitting, onAuthError],
    );

    return {
        isSubmitting: false,
        handler: show,
    };
};

export default useUpdateProjectAccountHandler;
